import { useCallback, useEffect, useState } from "react";

const useImageCache = (assets, setPreloadedAssets, batchSize = 5) => {
  const [imageCache, setImageCache] = useState(new Map());
  const [currentBatch, setCurrentBatch] = useState(0);
  const [preloadingBatch, setPreloadingBatch] = useState(false);

  // Extract image URLs from assets
  const imageUrls =
    assets
      ?.filter((item) => item.imageUrl)
      .map((item) => {
        if (typeof item.imageUrl === 'object' && item.imageUrl.default) {
          return item.imageUrl.default;
        } else if (typeof item.imageUrl === 'string') {
          return item.imageUrl;
        }
        return null;
      })
      .filter(Boolean) || [];

  // Batch preload images
  const preloadImageBatch = useCallback(
    async (batchNumber) => {
      if (preloadingBatch || !imageUrls.length) return;

      setPreloadingBatch(true);
      const startIndex = batchNumber * batchSize;
      const endIndex = Math.min(startIndex + batchSize, imageUrls.length);
      const batchUrls = imageUrls.slice(startIndex, endIndex);

      const preloadPromises = batchUrls.map((url) => {
        return new Promise((resolve) => {
          if (imageCache.has(url)) {
            resolve(url);
            return;
          }

          const img = new Image();
          img.onload = () => {
            setImageCache((prev) => new Map(prev).set(url, img));
            resolve(url);
          };
          img.onerror = () => {
            console.warn(`Failed to cache image: ${url}`);
            resolve(url);
          };
          img.src = url;
        });
      });

      await Promise.all(preloadPromises);
      setCurrentBatch(batchNumber);
      setPreloadingBatch(false);
    },
    [imageUrls, batchSize, preloadingBatch, imageCache],
  );

  // Calculate total batches needed
  const totalBatches = Math.ceil(imageUrls.length / batchSize);

  // Main preloading effect
  useEffect(() => {
    if (!Array.isArray(assets) || assets.length === 0) return;

    // Start preloading first batch of images
    if (imageUrls.length > 0 && currentBatch === 0 && imageCache.size === 0) {
      preloadImageBatch(0);
    }
  }, [assets, imageUrls.length, currentBatch, imageCache.size]);

  // Progressive batch loading effect
  useEffect(() => {
    if (imageUrls.length > 0 && currentBatch < totalBatches - 1) {
      // Preload next batch after a small delay
      const timeoutId = setTimeout(() => {
        preloadImageBatch(currentBatch + 1);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [currentBatch, totalBatches, imageUrls.length, preloadImageBatch]);

  // Update preloaded assets when cache changes
  useEffect(() => {
    const preloadedAssets = {
      images: imageCache, // Return the Map directly
    };
    setPreloadedAssets(preloadedAssets);
  }, [imageCache, setPreloadedAssets]);

  // Return loading state and progress info
  return {
    isPreloading: preloadingBatch,
    imagesLoaded: imageCache.size,
    totalImages: imageUrls.length,
    currentBatch,
    totalBatches,
    progress: imageUrls.length > 0 ? (imageCache.size / imageUrls.length) * 100 : 100,
  };
};
export default useImageCache;